/**
 * 文件动作（M2-03 / DESIGN.md §2.6）。
 *
 * 新建/打开/保存/导出/恢复五个动作在这里注册。所有外设能力都是注入的：
 * FileGateway（M1-16）、编解码（M1-14）、.cvsm（M1-15）、OPFS 工作区镜像。
 * 测试用假实现驱动全部路径，不碰真实浏览器 API。
 *
 * 保存语义（§2.6）：
 * - FSA 环境：第一次保存弹「另存为」，之后 Ctrl+S 用句柄**原地覆盖**；
 * - 降级环境：每次保存都是下载一份 `.cvsm` 工程文件，动作标题显示
 *   「下载副本…」（saveActionLabel，这正是能力矩阵在 UI 上的样子；
 *   ADR-0016：不叫「导出为…」是为了不跟真正的图片导出撞名）。
 * - 每次成功保存都镜像到 OPFS 工作区，崩溃后可恢复。
 */
import { decodeImage, encodeImage } from '../../io/codecs';
import { loadCvsm, saveCvsm, type SaveResult } from '../../io/cvsm';
import {
  isAbortError,
  saveActionLabel,
  throwIfAborted,
  type FileGateway,
  type FileHandleRef,
  type OpenedFile,
} from '../../io/gateway';
import type { OpfsWorkspace } from '../../io/gateway';
import { compositeDocument } from '../composite';
import type { AppStore } from '../store';
import type { ActionRegistry } from '../actions/registry';
import { PHOTOSHOP_ACTION_SHORTCUTS } from '../actions/keymap';
import { documentFromImage } from './image-import';
import { createBlankDocument } from './new-document';
import { fromCvsmDocument, toCvsmDocument } from './cvsm-bridge';
import { importPsd } from './psd-import';

export interface FileDeps {
  readonly store: AppStore;
  readonly gateway: FileGateway;
  /** OPFS 不可用时为 null——没有崩溃恢复，但编辑器照常工作。 */
  readonly workspace: OpfsWorkspace | null;
  readonly now: () => string;
  /** 注入点：测试给假实现。 */
  readonly decode?: typeof decodeImage | undefined;
  readonly encode?: typeof encodeImage | undefined;
  readonly newDocumentId?: (() => string) | undefined;
  /**
   * 「文件 → 新建」现在先弹尺寸对话框（参照 Photoshop），真正建文档
   * 是对话框确定之后的事——这个动作本身只负责"把对话框打开"，装配层
   * （main.tsx）接住这个回调去控制显隐 signal。
   */
  readonly onRequestNewDocument?: (() => void) | undefined;
}

export interface FileCallOptions {
  readonly signal?: AbortSignal;
}

/** 一个打开文档的保存上下文：覆盖句柄与上次保存结果（增量保存用）。 */
export class FileSession {
  handle: FileHandleRef | null = null;
  lastSave: SaveResult | null = null;
  fileName = '未命名.cvsm';

  reset(fileName: string): void {
    this.handle = null;
    this.lastSave = null;
    this.fileName = fileName;
  }
}

const OPEN_ACCEPT = ['.png', '.jpg', '.jpeg', '.cvsm', '.psd'] as const;

/** `.cvsm` 的 ZIP 魔数。按内容判断而不是只看扩展名——文件名是用户可控的。 */
function looksLikeCvsm(file: OpenedFile): boolean {
  return (
    file.name.toLowerCase().endsWith('.cvsm') &&
    file.bytes[0] === 0x50 &&
    file.bytes[1] === 0x4b
  );
}

/** PSD 的 `8BPS` 魔数，同样按内容判断而不是只看扩展名。 */
function looksLikePsd(file: OpenedFile): boolean {
  return (
    file.bytes[0] === 0x38 && // '8'
    file.bytes[1] === 0x42 && // 'B'
    file.bytes[2] === 0x50 && // 'P'
    file.bytes[3] === 0x53 // 'S'
  );
}

/* ------------------------------------------------------------------ */
/* 服务函数：动作的可测试内核，全部接受 AbortSignal                        */
/* ------------------------------------------------------------------ */

export async function openFromGateway(
  deps: FileDeps,
  session: FileSession,
  options: FileCallOptions = {},
): Promise<boolean> {
  throwIfAborted(options.signal);
  const opened = await deps.gateway.open(OPEN_ACCEPT, options);
  const file = opened[0];
  if (file === undefined) return false; // 用户取消

  throwIfAborted(options.signal);
  if (looksLikeCvsm(file)) {
    const loaded = await loadCvsm(file.bytes);
    throwIfAborted(options.signal);
    const document = fromCvsmDocument(loaded, deps.store.tiles, deps.now);
    deps.store.setDocument(document);
    session.reset(file.name);
    session.handle = file.handle; // .cvsm 可以原地覆盖
    return true;
  }

  if (looksLikePsd(file)) {
    const { document, warnings } = await importPsd(file.bytes, deps.store.tiles, {
      id: `doc-${deps.now()}`,
      fileName: file.name,
      now: deps.now,
    });
    throwIfAborted(options.signal);
    deps.store.setDocument(document);
    // PSD 导入不保证 round-trip（ADR-0015），不能原地覆盖成同一份 .psd
    session.reset(`${file.name.replace(/\.[^.]+$/, '')}.cvsm`);
    if (warnings.length > 0) {
      deps.store.setHint(`已打开，以下图层做了简化处理：${warnings.join('；')}`);
    }
    return true;
  }

  const decode = deps.decode ?? decodeImage;
  const image = await decode(
    file.bytes,
    options.signal === undefined ? {} : { signal: options.signal },
  );
  throwIfAborted(options.signal);
  const document = documentFromImage(image, deps.store.tiles, {
    id: `doc-${deps.now()}`,
    fileName: file.name,
    now: deps.now,
  });
  deps.store.setDocument(document);
  // 图片来源不能原地覆盖成 .cvsm，保存时走「另存为」
  session.reset(`${file.name.replace(/\.[^.]+$/, '')}.cvsm`);
  return true;
}

export async function saveToGateway(
  deps: FileDeps,
  session: FileSession,
  options: FileCallOptions = {},
): Promise<boolean> {
  throwIfAborted(options.signal);
  const document = deps.store.document;
  if (document === null) return false;

  const cvsm = toCvsmDocument(document, deps.store.tiles);
  const result = await saveCvsm(cvsm, { previous: session.lastSave });
  throwIfAborted(options.signal);

  const handle = await deps.gateway.save(
    { name: session.fileName, bytes: result.bytes },
    session.handle,
    options,
  );
  if (handle === null && deps.gateway.canOverwrite) return false; // 用户取消了另存为
  session.handle = handle;
  session.lastSave = result;

  // 镜像到工作区：崩溃恢复的数据源。镜像失败不影响保存本身，但要让人知道
  if (deps.workspace !== null) {
    try {
      await deps.workspace.save({ name: session.fileName, bytes: result.bytes });
    } catch (error) {
      deps.store.setHint(`自动保存镜像失败：${error instanceof Error ? error.message : String(error)}`);
    }
  }
  return true;
}

export async function exportPng(
  deps: FileDeps,
  session: FileSession,
  options: FileCallOptions = {},
): Promise<boolean> {
  throwIfAborted(options.signal);
  const document = deps.store.document;
  if (document === null) return false;

  const flat = compositeDocument(document, deps.store.tiles);
  throwIfAborted(options.signal);
  const encode = deps.encode ?? encodeImage;
  const bytes = await encode('png', flat.pixels, {
    width: flat.width,
    height: flat.height,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  throwIfAborted(options.signal);
  const name = `${session.fileName.replace(/\.cvsm$/, '')}.png`;
  // 导出永远是「另存为」语义，不复用 .cvsm 的句柄
  await deps.gateway.save({ name, bytes, mimeType: 'image/png' }, null, options);
  return true;
}

export interface NewDocumentOptions {
  readonly width: number;
  readonly height: number;
  readonly title: string;
  /** sRGB 0–255，不透明背景色。省略 = 白（跟 createBlankDocument 同一个缺省）。 */
  readonly background?: readonly [number, number, number] | undefined;
  /** 背景图层整片 alpha=0，忽略 `background`。 */
  readonly transparent?: boolean | undefined;
}

/**
 * 新建文档对话框确定之后真正落地——纯色背景图层（跟 documentFromImage
 * 同一条「装文档」通道：直接 setDocument，不走 DocumentDraft/CommandBus，
 * 新建本身不是一次可撤销的编辑）。
 */
export function createNewDocument(
  deps: FileDeps,
  session: FileSession,
  options: NewDocumentOptions,
): void {
  const document = createBlankDocument(deps.store.tiles, {
    id: deps.newDocumentId?.() ?? `doc-${deps.now()}`,
    width: options.width,
    height: options.height,
    title: options.title,
    createdAt: deps.now(),
    background: options.background,
    transparent: options.transparent,
  });
  deps.store.setDocument(document);
  session.reset(`${options.title}.cvsm`);
}

/** 启动时探测崩溃镜像。只报告 intact 的条目——半截数据让用户决定没有意义。 */
export async function bootRecovery(deps: FileDeps): Promise<void> {
  if (deps.workspace === null) return;
  const entries = await deps.workspace.listRecoverable();
  deps.store.setRecoverable(entries.filter((entry) => entry.intact));
}

export async function recoverLatest(deps: FileDeps, session: FileSession): Promise<boolean> {
  const entry = deps.store.recoverable[0];
  if (entry === undefined) return false;
  const loaded = await loadCvsm(entry.bytes);
  const document = fromCvsmDocument(loaded, deps.store.tiles, deps.now);
  deps.store.setDocument(document);
  session.reset(entry.name);
  deps.store.setHint(`已恢复「${entry.name}」（${entry.savedAt}）`);
  return true;
}

/* ------------------------------------------------------------------ */
/* 注册                                                                 */
/* ------------------------------------------------------------------ */

export function registerFileActions(registry: ActionRegistry, deps: FileDeps): FileSession {
  const session = new FileSession();

  registry.register({
    id: 'file.new',
    title: '新建…',
    group: '文件',
    section: 0,
    order: 0,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['file.new'],
    run: () => {
      deps.onRequestNewDocument?.();
    },
  });

  /**
   * 文件动作的统一错误面：ActionRegistry.execute 对 run 返回的 Promise 是
   * `void action.run(...)`——任何 rejection 都会变成 unhandled rejection，
   * 用户看到的只有"点了没反应"（真实反馈：打开一张解码失败的图片时
   * 界面毫无提示）。取消（AbortError）是正常路径静默放过，其余错误
   * 一律进状态栏提示。
   */
  const reportFailure = (verb: string, error: unknown): void => {
    if (isAbortError(error)) return; // 用户取消 picker，不是错误
    deps.store.setHint(`${verb}失败：${error instanceof Error ? error.message : String(error)}`);
  };

  registry.register({
    id: 'file.open',
    title: '打开…',
    group: '文件',
    section: 0,
    order: 1,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['file.open'],
    run: async () => {
      try {
        await openFromGateway(deps, session);
      } catch (error) {
        reportFailure('打开', error);
      }
    },
  });

  registry.register({
    id: 'file.recover',
    title: '恢复上次会话…',
    group: '文件',
    section: 0,
    order: 2,
    enabled: (s) => s.hasRecoverable,
    run: async () => {
      try {
        await recoverLatest(deps, session);
      } catch (error) {
        reportFailure('恢复', error);
      }
    },
  });

  registry.register({
    id: 'file.save',
    // 能力矩阵直接反映在动词上（§2.6）：能覆盖叫保存，只能下载叫「下载副本…」
    title: saveActionLabel(deps.gateway),
    group: '文件',
    section: 1,
    order: 0,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['file.save'],
    enabled: (s) => s.hasDocument,
    run: async (services) => {
      try {
        if (await saveToGateway(deps, session)) services.hint(`已保存 ${session.fileName}`);
      } catch (error) {
        reportFailure('保存', error);
      }
    },
  });

  registry.register({
    id: 'file.exportPng',
    title: '导出为 PNG…',
    group: '文件',
    section: 1,
    order: 1,
    enabled: (s) => s.hasDocument,
    run: async (services) => {
      try {
        if (await exportPng(deps, session)) services.hint('已导出 PNG');
      } catch (error) {
        reportFailure('导出', error);
      }
    },
  });

  return session;
}
